// Zod DTOs for the `/v1/resources` authoring surface (spec §3.3 `Resource`
// model, §3.4 `Resource.payload` shapes, §6.2). `tabId` is a path param on
// create (`POST /v1/tabs/:id/resources`); `position` is server-managed
// (append order — never client-supplied on create). `isPublished` is
// server-managed on CREATE (always starts `false`, matching `ResourceTab`/
// `Section`'s own create defaults) but IS a writable field on
// `resourceUpdateRequestSchema` (P3-BUG-1 fix — mirrors
// `resource-tab.ts`'s `resourceTabUpdateRequestSchema.isPublished`; there is
// no dedicated per-resource publish route, same direct-field pattern
// `PATCH /v1/tabs/:id` already uses instead of a `/publish` verb).
//
// `payload` is a **discriminated union keyed by `type`** (spec §3.4's
// table): the request schema below picks the exact payload shape for the
// resource's `ResourceType`, so e.g. a `VIDEO` create can never carry a
// `PDF`-shaped payload — zod rejects it with a field-level error on
// `payload` rather than silently accepting an unrelated shape.
import { z } from 'zod';
import { ResourceType } from '../enums';
import { idSchema, positionSchema } from './primitives';

const titleSchema = z.string().trim().min(1, 'Title is required').max(200);
const descriptionSchema = z.string().trim().max(2000).optional();
/** Free-text sub-classification (spec §3.3 comment: "WORKSHEET | LAB_SHEET |
 * MOCK | MARK_SCHEME") — not a Prisma enum, so modelled the same way
 * `ExamBoard.code`/`Level.code` are (spec §3.5 "No enums" philosophy applied
 * consistently): bounded free text, not a fixed literal set. */
const subtypeSchema = z.string().trim().min(1).max(60).optional();
const durationSecSchema = z.number().int().positive().optional();

// ─── §3.4 per-type payload shapes ───────────────────────────────────────────

export const noteResourcePayloadSchema = z.object({
  format: z.enum(['tiptap-json', 'markdown']),
  body: z.union([z.string(), z.record(z.string(), z.unknown())]),
  attachments: z.array(idSchema).default([]),
});
export type NoteResourcePayload = z.infer<typeof noteResourcePayloadSchema>;

export const videoResourcePayloadSchema = z.object({
  source: z.enum(['youtube', 'vimeo', 'r2-hls', 'mux', 'mp4']),
  externalId: z.string().trim().min(1).optional(),
  playbackId: z.string().trim().min(1).optional(),
  posterAssetId: idSchema.optional(),
});
export type VideoResourcePayload = z.infer<typeof videoResourcePayloadSchema>;

/** P2-19 — one step of a SIMULATION resource's optional guidance block
 * (`{whatToNotice, tasks}` on {@link simulationResourcePayloadSchema}).
 * `index`/`total` are caller-supplied (not derived from array position), so
 * the schema only bounds them to positive integers — it does not
 * cross-check `index <= total` or that `index` values are
 * contiguous/unique across the `tasks` array; that ordering/consistency
 * concern belongs to the authoring UI, not this DTO. */
export const simulationTaskSchema = z.object({
  index: z.number().int().positive(),
  total: z.number().int().positive(),
  prompt: z.string().trim().min(1),
});
export type SimulationTask = z.infer<typeof simulationTaskSchema>;

export const simulationResourcePayloadSchema = z
  .object({
    mode: z.enum(['iframe', 'bundle', 'r2-bundle']),
    url: z.string().trim().url().optional(),
    bundleAssetId: idSchema.optional(),
    /** `SimulationBundle.id` — required iff `mode === 'r2-bundle'`. Distinct
     * from `bundleAssetId` (which resolves an `Asset`, the pre-existing
     * single-file `'bundle'` mode) precisely so the two resolvers
     * (`resolvePublicAssetUrl` for `bundleAssetId`, the R2-bundle serve path
     * for `bundleId`) stay total and honest — see
     * `docs/design/simulation-upload-design.md` §4.2. */
    bundleId: idSchema.optional(),
    aspectRatio: z.string().trim().min(1).max(16).optional(),
    sandbox: z.array(z.string().trim().min(1)).optional(),
    /** P2-19 — additive/optional guidance block surfaced as a read-only
     * projection on `GET /v1/me/resources/:id` (consumed by the Simulation
     * Player, P2-17). Purely additive: existing stored SIMULATION rows
     * written before this block existed simply omit both fields and remain
     * valid — no migration/backfill needed since `payload` is a JSON string
     * decoded in the service layer (P2-01), not a typed column. */
    whatToNotice: z.string().trim().min(1).optional(),
    tasks: z.array(simulationTaskSchema).optional(),
  })
  // QA-CCFM-03 — `mode` selects which of `url`/`bundleAssetId` is the
  // resource's actual content source; without this refinement both fields
  // are individually optional and a `{"mode":"iframe"}` payload (no `url`,
  // no `bundleAssetId`) passed validation and saved an unplayable/blank
  // SIMULATION resource. `path` targets the specific missing field so the
  // RFC7807 problem+json `detail` names `payload.url`/`payload.bundleAssetId`
  // (see `defaultTabsSchema` in `./level.ts` for the same `.superRefine` +
  // `ctx.addIssue` convention used here).
  .superRefine((val, ctx) => {
    if (val.mode === 'iframe' && !val.url) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '"url" is required when mode is "iframe"',
        path: ['url'],
      });
    }
    if (val.mode === 'bundle' && !val.bundleAssetId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '"bundleAssetId" is required when mode is "bundle"',
        path: ['bundleAssetId'],
      });
    }
    // r2-bundle: `bundleId` (a SimulationBundle.id) is the mode's sole
    // content-source field, mirroring the `url`/`bundleAssetId` checks above
    // (design doc §4.2). Also enforced: only the active mode's field(s) may
    // be present, so a payload can't ambiguously reference two content
    // sources at once (the same "never both" invariant `me-resource.ts`'s
    // `projectSimulationPayload` relies on, I-4).
    if (val.mode === 'r2-bundle' && !val.bundleId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '"bundleId" is required when mode is "r2-bundle"',
        path: ['bundleId'],
      });
    }
    if (val.mode === 'r2-bundle' && val.url) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '"url" must not be set when mode is "r2-bundle"',
        path: ['url'],
      });
    }
    if (val.mode === 'r2-bundle' && val.bundleAssetId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '"bundleAssetId" must not be set when mode is "r2-bundle"',
        path: ['bundleAssetId'],
      });
    }
    if (val.mode !== 'r2-bundle' && val.bundleId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '"bundleId" must not be set unless mode is "r2-bundle"',
        path: ['bundleId'],
      });
    }
  });
export type SimulationResourcePayload = z.infer<typeof simulationResourcePayloadSchema>;

export const pdfResourcePayloadSchema = z.object({
  assetId: idSchema,
  pageCount: z.number().int().positive().optional(),
  watermark: z.boolean().optional(),
});
export type PdfResourcePayload = z.infer<typeof pdfResourcePayloadSchema>;

export const linkResourcePayloadSchema = z.object({
  url: z.string().trim().url(),
  openInNewTab: z.boolean().optional(),
});
export type LinkResourcePayload = z.infer<typeof linkResourcePayloadSchema>;

// ─── POST /v1/tabs/:id/resources ────────────────────────────────────────────

const resourceBaseFields = {
  title: titleSchema,
  description: descriptionSchema,
  subtype: subtypeSchema,
  assetId: idSchema.optional(),
  durationSec: durationSecSchema,
};

export const resourceCreateRequestSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(ResourceType.NOTE),
    ...resourceBaseFields,
    payload: noteResourcePayloadSchema,
  }),
  z.object({
    type: z.literal(ResourceType.VIDEO),
    ...resourceBaseFields,
    payload: videoResourcePayloadSchema,
  }),
  z.object({
    type: z.literal(ResourceType.SIMULATION),
    ...resourceBaseFields,
    payload: simulationResourcePayloadSchema,
  }),
  z.object({
    type: z.literal(ResourceType.PDF),
    ...resourceBaseFields,
    payload: pdfResourcePayloadSchema,
  }),
  z.object({
    type: z.literal(ResourceType.LINK),
    ...resourceBaseFields,
    payload: linkResourcePayloadSchema,
  }),
]);
export type ResourceCreateRequest = z.infer<typeof resourceCreateRequestSchema>;

// ─── PATCH /v1/resources/:id ─────────────────────────────────────────────────
// `type` stays required in an update too (it selects which payload shape the
// (optional) `payload` replacement must satisfy) — but every other field is
// optional, so a caller can patch e.g. just `title` without resending the
// full payload.

const resourceUpdateBaseFields = {
  title: titleSchema.optional(),
  description: descriptionSchema,
  subtype: subtypeSchema,
  assetId: idSchema.optional(),
  durationSec: durationSecSchema,
  /** P3-BUG-1 — direct explicit publish flag, same shape as
   * `@eskayscience/shared/dto/resource-tab.ts`'s `resourceTabUpdateRequestSchema.isPublished`
   * (PATCH /v1/tabs/:id already accepts this exact field the same way).
   * `Resource.isPublished` previously had NO mutation path at all: this
   * file's own header comment used to say "isPublished are server-managed
   * ... resources publish implicitly with their parent tab/section" but no
   * code anywhere ever actually set it true, so every resource was stuck at
   * Prisma's `@default(false)` forever and no student could ever see any
   * authored content (`/v1/me/*` hard-filters `isPublished: true`). Mirrors
   * the ResourceTab precedent instead of inventing a new mechanism. */
  isPublished: z.boolean().optional(),
  /** FR-4 — direct explicit free-preview flag, same "no dedicated verb
   * route, direct writable field on the update DTO" pattern as
   * `isPublished` immediately above (P3-BUG-1). `Resource.isFreePreview`
   * (P2-18) already existed on the Prisma model and was already surfaced
   * read-only in `toResourceResponse`/`ResourceResponse`, but — same bug
   * class as P3-BUG-1 — had no mutation path at all, so it was stuck at
   * its `@default(false)` forever. Bypasses the normal enrollment/
   * subscription/access-grant gate for an unauthenticated or non-entitled
   * visitor once BOTH this and `isPublished` are `true` (see
   * `../../../apps/api/src/services/meContent.ts` / `routes/openapi.ts`'s
   * "Anonymous catalogue" doc-comment for the read-side consumer). */
  isFreePreview: z.boolean().optional(),
};

export const resourceUpdateRequestSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(ResourceType.NOTE),
    ...resourceUpdateBaseFields,
    payload: noteResourcePayloadSchema.optional(),
  }),
  z.object({
    type: z.literal(ResourceType.VIDEO),
    ...resourceUpdateBaseFields,
    payload: videoResourcePayloadSchema.optional(),
  }),
  z.object({
    type: z.literal(ResourceType.SIMULATION),
    ...resourceUpdateBaseFields,
    payload: simulationResourcePayloadSchema.optional(),
  }),
  z.object({
    type: z.literal(ResourceType.PDF),
    ...resourceUpdateBaseFields,
    payload: pdfResourcePayloadSchema.optional(),
  }),
  z.object({
    type: z.literal(ResourceType.LINK),
    ...resourceUpdateBaseFields,
    payload: linkResourcePayloadSchema.optional(),
  }),
]);
export type ResourceUpdateRequest = z.infer<typeof resourceUpdateRequestSchema>;

// ─── POST /v1/tabs/:id/resources/bulk-publish (PUB-no-bulk-publish-action-for-resources) ──
// Bulk publish/unpublish across up to 50 resources in ONE tab in a single
// request — the one-at-a-time `PATCH /v1/resources/:id { isPublished }`
// path above is the only mutation route today, and is too tedious for
// populating a full ~22-resource chapter. `resourceIds` is capped at 50
// (hard reject over-cap, 422 VALIDATION_FAILED — same status/code every
// other body-shape failure on this file's schemas already uses) so a
// single request can never touch an unbounded number of rows; a caller
// wanting more just issues another request. An empty array is rejected
// too (`min(1)`) — a bulk request that changes nothing is a malformed
// request, not a silent no-op.
export const resourceBulkPublishRequestSchema = z.object({
  resourceIds: z.array(idSchema).min(1).max(50),
  isPublished: z.boolean(),
});
export type ResourceBulkPublishRequest = z.infer<typeof resourceBulkPublishRequestSchema>;

// ─── POST /v1/resources/reorder ─────────────────────────────────────────────

export { reorderRequestSchema as resourceReorderRequestSchema } from './primitives';
export type { ReorderRequest as ResourceReorderRequest } from './primitives';

// Re-exported for callers that need to validate/derive a schema from a
// runtime `type` value (e.g. an admin bulk-import tool) without importing
// `../enums` separately.
export const resourceTypeSchema = z.enum(
  Object.values(ResourceType) as [ResourceType, ...ResourceType[]],
);
export { positionSchema as resourcePositionSchema };
